/**
 * Celebration Animation Component for Little Genius Hub
 * Displays confetti and celebration effects when kids complete achievements
 */

import React, { useEffect, useState, useCallback } from 'react';

// Confetti particle class
class Particle {
  constructor(canvasWidth, canvasHeight) {
    this.x = Math.random() * canvasWidth;
    this.y = -20;
    this.size = Math.random() * 10 + 5;
    this.speedY = Math.random() * 3 + 2;
    this.speedX = Math.random() * 4 - 2;
    this.rotation = Math.random() * 360;
    this.rotationSpeed = Math.random() * 10 - 5;
    this.color = this.getRandomColor();
    this.shape = Math.floor(Math.random() * 3); // 0: circle, 1: square, 2: star
    this.opacity = 1;
  }

  getRandomColor() {
    const colors = [
      '#FF6B6B', // Red
      '#4ECDC4', // Teal
      '#45B7D1', // Blue
      '#96CEB4', // Green
      '#FFEAA7', // Yellow
      '#DDA0DD', // Plum
      '#98D8C8', // Mint
      '#F7DC6F', // Gold
      '#BB8FCE', // Purple
      '#85C1E9', // Sky blue
      '#F8B500', // Orange
      '#FF69B4', // Pink
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  update(canvasHeight) {
    this.y += this.speedY;
    this.x += this.speedX;
    this.rotation += this.rotationSpeed;
    this.speedY += 0.1; // gravity
    
    // Fade out near bottom
    if (this.y > canvasHeight - 100) {
      this.opacity = Math.max(0, 1 - (this.y - (canvasHeight - 100)) / 100);
    }
  }

  draw(ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.globalAlpha = this.opacity;
    ctx.fillStyle = this.color;

    switch (this.shape) {
      case 0: // Circle
        ctx.beginPath();
        ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 1: // Square
        ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        break;
      case 2: // Star
        this.drawStar(ctx, 0, 0, 5, this.size / 2, this.size / 4);
        break;
      default:
        ctx.beginPath();
        ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
        ctx.fill();
    }

    ctx.restore();
  }

  drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
    let rot = (Math.PI / 2) * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);

    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }

    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
  }
}

const CelebrationAnimation = ({ 
  show, 
  onComplete,
  duration = 3000,
  particleCount = 100,
  message = "Amazing!" 
}) => {
  const [particles, setParticles] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const canvasRef = React.useRef(null);

  const initParticles = useCallback((width, height) => {
    const newParticles = [];
    for (let i = 0; i < particleCount; i++) {
      // Stagger particle creation for wave effect
      setTimeout(() => {
        newParticles.push(new Particle(width, height));
        setParticles([...newParticles]);
      }, i * 20);
    }
  }, [particleCount]);

  useEffect(() => {
    if (!show) {
      setParticles([]);
      setShowMessage(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    canvas.width = width;
    canvas.height = height;

    // Initialize particles
    initParticles(width, height);
    
    // Show message after a short delay
    setTimeout(() => setShowMessage(true), 300);

    // Animation loop
    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      setParticles(prev => {
        const updated = prev.filter(p => p.y < height && p.opacity > 0);
        updated.forEach(particle => {
          particle.update(height);
          particle.draw(ctx);
        });
        return updated;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // End celebration after duration
    const timer = setTimeout(() => {
      cancelAnimationFrame(animationId);
      setShowMessage(false);
      if (onComplete) onComplete();
    }, duration);

    return () => {
      cancelAnimationFrame(animationId);
      clearTimeout(timer);
    };
  }, [show, duration, initParticles, onComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ pointerEvents: 'none' }}
      />
      
      {/* Celebration Message */}
      {showMessage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="text-center animate-bounce"
            style={{
              animation: 'celebrationPop 0.5s ease-out forwards, celebrationFloat 2s ease-in-out infinite 0.5s'
            }}
          >
            <div className="text-6xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 drop-shadow-lg"
              style={{
                textShadow: '0 0 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,200,0,0.6)'
              }}
            >
              {message}
            </div>
            <div className="text-4xl mt-4">
              🎉✨🌟✨🎉
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes celebrationPop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes celebrationFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
};

// Simple emoji burst for smaller celebrations (correct answers)
export const MiniBurst = ({ show, emoji = "⭐", x, y }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!show) {
      setParticles([]);
      return;
    }

    const newParticles = [];
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      newParticles.push({
        id: i,
        x: 0,
        y: 0,
        targetX: Math.cos(angle) * 60,
        targetY: Math.sin(angle) * 60,
        emoji: ['⭐', '✨', '💫', '🌟'][Math.floor(Math.random() * 4)]
      });
    }
    setParticles(newParticles);

    const timer = setTimeout(() => setParticles([]), 800);
    return () => clearTimeout(timer);
  }, [show]);

  if (!show || particles.length === 0) return null;

  return (
    <div 
      className="fixed pointer-events-none z-40"
      style={{ left: x || '50%', top: y || '50%', transform: 'translate(-50%, -50%)' }}
    >
      {particles.map(p => (
        <span
          key={p.id}
          className="absolute text-2xl"
          style={{
            animation: 'burstOut 0.6s ease-out forwards',
            '--tx': `${p.targetX}px`,
            '--ty': `${p.targetY}px`
          }}
        >
          {p.emoji}
        </span>
      ))}
      <style>{`
        @keyframes burstOut {
          0% {
            transform: translate(0, 0) scale(0);
            opacity: 1;
          }
          100% {
            transform: translate(var(--tx), var(--ty)) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CelebrationAnimation;
